import { useEffect, useRef, useState } from "react";
import {
  Loader2,
  X,
  Save,
  Trash2,
  Upload,
  AlertTriangle,
  Plus,
  ImageIcon,
} from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { api, formatApiErrorDetail } from "../lib/api";

const CATEGORIES = [
  { id: "doe-vendor", label: "DOE Vendor" },
  { id: "nyc-vendor", label: "NYC City Vendor" },
  { id: "mwbe-certified", label: "MWBE Certified" },
  { id: "queens-based", label: "Queens Based" },
  { id: "workforce-development", label: "Workforce Development" },
  { id: "restaurant-catering", label: "Restaurant Catering" },
];

const emptyForm = () => ({
  title: "",
  date: new Date().toISOString().slice(0, 10),
  category: "queens-based",
  description: "",
  images: [],
  tags: [],
  is_published: true,
});

/**
 * Editor modal for gallery / experience entries.
 *
 * Props:
 *   open, onOpenChange
 *   experience: existing entry (edit) or null (create)
 *   onSaved: () => void  — called after a successful save
 *   onDeleted: () => void
 */
export const ExperienceEditorModal = ({ open, onOpenChange, experience, onSaved, onDeleted }) => {
  const mode = experience ? "edit" : "create";
  const [form, setForm] = useState(emptyForm());
  const [tagsInput, setTagsInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (experience) {
      setForm({
        title: experience.title || "",
        date: experience.date || new Date().toISOString().slice(0, 10),
        category: experience.category || "queens-based",
        description: experience.description || "",
        images: experience.images || [],
        tags: experience.tags || [],
        is_published: experience.is_published !== false,
      });
      setTagsInput((experience.tags || []).join(", "));
    } else {
      setForm(emptyForm());
      setTagsInput("");
    }
    setError("");
  }, [open, experience]);

  const update = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setForm((s) => ({ ...s, [k]: v }));
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      setError("Image must be 8MB or smaller.");
      return;
    }
    setUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/admin/experiences/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const apiBase = process.env.REACT_APP_BACKEND_URL || "";
      const fullUrl = res.data.url.startsWith("http")
        ? res.data.url
        : `${apiBase}${res.data.url}`;
      setForm((s) => ({ ...s, images: [...s.images, fullUrl] }));
    } catch (err) {
      setError(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          err?.message ||
          "Upload failed"
      );
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeImage = (idx) =>
    setForm((s) => ({ ...s, images: s.images.filter((_, i) => i !== idx) }));

  const moveImage = (idx, dir) => {
    setForm((s) => {
      const nextImages = [...s.images];
      const swap = idx + dir;
      if (swap < 0 || swap >= nextImages.length) return s;
      [nextImages[idx], nextImages[swap]] = [nextImages[swap], nextImages[idx]];
      return { ...s, images: nextImages };
    });
  };

  const validate = () => {
    if (!form.title.trim()) return "Title is required.";
    if (!form.date) return "Date is required.";
    if (!form.description.trim() || form.description.trim().length < 10)
      return "Description must be at least 10 characters.";
    if (!CATEGORIES.find((c) => c.id === form.category)) return "Pick a valid category.";
    return "";
  };

  const submit = async () => {
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    const tags = tagsInput
      .split(/[,\n]/)
      .map((t) => t.trim().replace(/^#/, ""))
      .filter(Boolean);
    setError("");
    setSaving(true);
    try {
      const payload = { ...form, tags };
      if (mode === "create") {
        await api.post("/admin/experiences", payload);
      } else {
        await api.patch(`/admin/experiences/${experience.id}`, payload);
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      setError(
        formatApiErrorDetail(e?.response?.data?.detail) || e?.message || "Could not save"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!experience?.id) return;
    if (!window.confirm(`Delete "${experience.title}"? This cannot be undone.`)) return;
    setSaving(true);
    try {
      await api.delete(`/admin/experiences/${experience.id}`);
      onDeleted?.();
      onOpenChange(false);
    } catch (e) {
      setError(
        formatApiErrorDetail(e?.response?.data?.detail) || e?.message || "Could not delete"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="experience-editor-modal"
        className="sm:max-w-[720px] max-h-[92vh] overflow-y-auto p-0 rounded-2xl border-none"
      >
        <div className="bg-brand-charcoal text-white px-6 py-5 sticky top-0 z-10">
          <DialogHeader className="space-y-1">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-brand-gold">
              {mode === "create" ? "New Gallery Post" : "Edit Gallery Post"}
            </p>
            <DialogTitle className="font-display font-black text-[22px] tracking-tight text-white text-left">
              {mode === "create" ? "Add an experience" : form.title || "Edit entry"}
            </DialogTitle>
            <DialogDescription className="text-[12.5px] text-white/65 text-left">
              Title, date, category, description, and at least one image. Goes live
              instantly on /gallery once saved.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="px-5 sm:px-6 py-5 space-y-5">
          {/* Images */}
          <section className="rounded-2xl bg-brand-cream/40 border border-black/5 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-black text-[14px] text-brand-charcoal">
                Images
              </h3>
              <button
                type="button"
                data-testid="exp-upload-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="inline-flex items-center gap-2 rounded-full bg-brand-charcoal text-white px-4 py-2 text-[11px] font-bold uppercase tracking-[0.1em] hover:bg-brand-teal transition-colors disabled:opacity-60"
              >
                {uploading ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Upload className="h-3.5 w-3.5" />
                )}
                {uploading ? "Uploading…" : "Upload Image"}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
                data-testid="exp-file-input"
              />
            </div>
            {form.images.length === 0 ? (
              <div
                data-testid="exp-no-images"
                className="rounded-xl border-2 border-dashed border-black/15 p-8 text-center text-[12.5px] text-brand-charcoal/60"
              >
                <ImageIcon className="h-6 w-6 mx-auto mb-2 text-brand-charcoal/30" />
                No images yet. Upload one — the first image becomes the primary thumbnail.
              </div>
            ) : (
              <ul
                data-testid="exp-images-list"
                className="grid grid-cols-2 sm:grid-cols-3 gap-2"
              >
                {form.images.map((src, idx) => (
                  <li
                    key={src}
                    data-testid={`exp-image-${idx}`}
                    className="relative aspect-square rounded-xl overflow-hidden bg-white border border-black/5 group"
                  >
                    <img src={src} alt="" className="absolute inset-0 w-full h-full object-cover" />
                    {idx === 0 && (
                      <span className="absolute top-1.5 left-1.5 inline-flex items-center rounded-full bg-brand-gold text-brand-charcoal px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.1em]">
                        Primary
                      </span>
                    )}
                    <div className="absolute inset-x-0 bottom-0 p-1.5 flex items-center justify-between gap-1 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => moveImage(idx, -1)}
                          disabled={idx === 0}
                          className="text-white/85 hover:text-white text-[11px] font-bold disabled:opacity-30"
                          aria-label="Move up"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          onClick={() => moveImage(idx, 1)}
                          disabled={idx === form.images.length - 1}
                          className="text-white/85 hover:text-white text-[11px] font-bold disabled:opacity-30"
                          aria-label="Move down"
                        >
                          ↓
                        </button>
                      </div>
                      <button
                        type="button"
                        data-testid={`exp-remove-image-${idx}`}
                        onClick={() => removeImage(idx)}
                        className="text-white/85 hover:text-red-300"
                        aria-label="Remove"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Core fields */}
          <section className="rounded-2xl bg-brand-cream/40 border border-black/5 p-4 grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <Field label="Title *">
                <Input data-testid="exp-title" value={form.title} onChange={update("title")} className="h-10 rounded-xl bg-white border-black/10" />
              </Field>
            </div>
            <Field label="Date *">
              <Input data-testid="exp-date" type="date" value={form.date} onChange={update("date")} className="h-10 rounded-xl bg-white border-black/10" />
            </Field>
            <Field label="Category *">
              <select
                data-testid="exp-category"
                value={form.category}
                onChange={update("category")}
                className="h-10 rounded-xl bg-white border border-black/10 px-3 text-[13px]"
              >
                {CATEGORIES.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
              </select>
            </Field>
            <div className="sm:col-span-2">
              <Field label="Description *">
                <Textarea
                  data-testid="exp-description"
                  value={form.description}
                  onChange={update("description")}
                  rows={4}
                  placeholder="2–3 sentences telling the story…"
                  className="rounded-xl bg-white border-black/10"
                />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <Field label="Hashtags / tags (comma-separated)">
                <Input
                  data-testid="exp-tags"
                  value={tagsInput}
                  onChange={(e) => setTagsInput(e.target.value)}
                  placeholder="e.g. community, queens, syep"
                  className="h-10 rounded-xl bg-white border-black/10"
                />
              </Field>
            </div>
            <div className="sm:col-span-2 flex items-center gap-2">
              <input
                id="exp-publish"
                type="checkbox"
                checked={form.is_published}
                onChange={(e) => setForm((s) => ({ ...s, is_published: e.target.checked }))}
                data-testid="exp-published"
                className="h-4 w-4 accent-brand-teal"
              />
              <label htmlFor="exp-publish" className="text-[12.5px] text-brand-charcoal/75">
                Published — visible on the public gallery
              </label>
            </div>
          </section>

          {error && (
            <div
              data-testid="exp-error"
              role="alert"
              className="inline-flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700"
            >
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              type="button"
              data-testid="exp-save-btn"
              onClick={submit}
              disabled={saving || uploading}
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-brand-charcoal text-white px-6 py-3 text-[12px] font-bold uppercase tracking-[0.12em] hover:bg-brand-teal transition-colors disabled:opacity-60"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {mode === "create" ? "Publish Post" : "Save Changes"}
            </button>
            {mode === "edit" && (
              <button
                type="button"
                data-testid="exp-delete-btn"
                onClick={handleDelete}
                disabled={saving || uploading}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-white border border-red-200 text-red-700 px-5 py-3 text-[12px] font-bold uppercase tracking-[0.12em] hover:bg-red-50 transition-colors disabled:opacity-60"
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </button>
            )}
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-white border border-black/10 text-brand-charcoal/70 px-5 py-3 text-[12px] font-bold uppercase tracking-[0.12em] hover:bg-brand-cream"
            >
              <X className="h-4 w-4" />
              Cancel
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, children }) => (
  <div className="flex flex-col gap-1.5">
    <Label className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/65">
      {label}
    </Label>
    {children}
  </div>
);

export default ExperienceEditorModal;
